/**
 * Promotion Popup Modal Component
 * Shows promotions in a popup - only displays if promotions exist
 */

import { createPromotionCard } from './promotion-card.js';

class PromotionPopup {
    constructor() {
        this.modal = document.getElementById('promoModal');
        this.content = document.getElementById('promoModalContent');
        this.closeBtn = document.getElementById('promoModalClose');
        this.promotions = [];
        this.currentIndex = 0;
        this.autoPlayInterval = null;
        this.autoPlayDelay = 5000;
    }

    async init() {
        if (!this.modal || !this.content) return;

        try {
            const response = await fetch('/api/promotions');
            const data = await response.json();
            
            console.log('Promotions API response:', data);

            // Handle response format: data.promotions (array)
            const promotions = data.promotions || [];
            
            if (response.ok && promotions.length > 0) {
                this.promotions = promotions;
                this.render();
                this.setupEvents();
                // Show popup after 0.5 seconds
                setTimeout(() => this.show(), 500);
            }
            // If no promotions, do nothing - modal stays hidden
        } catch (error) {
            console.error('Failed to load promotions:', error);
            // Silently fail - don't show anything
        }
    }

    render() {
        this.content.innerHTML = `
            <div class="promo-popup-header">
                <h2>🎉 Upcoming Events</h2>
            </div>
            <div class="promo-popup-carousel">
                <div class="promo-popup-track" id="promoPopupTrack"></div>
                ${this.promotions.length > 1 ? `
                    <button class="promo-popup-nav promo-popup-prev" aria-label="Previous">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <button class="promo-popup-nav promo-popup-next" aria-label="Next">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                ` : ''}
            </div>
            ${this.promotions.length > 1 ? `
                <div class="promo-popup-dots" id="promoPopupDots"></div>
            ` : ''}
        `;

        const track = document.getElementById('promoPopupTrack');
        const dots = document.getElementById('promoPopupDots');

        this.promotions.forEach((promo, index) => {
            const card = createPromotionCard(promo);
            if (index === 0) card.classList.add('active');
            track.appendChild(card);

            if (dots) {
                const dot = document.createElement('button');
                dot.className = `promo-popup-dot ${index === 0 ? 'active' : ''}`;
                dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
                dot.addEventListener('click', () => this.goToSlide(index));
                dots.appendChild(dot);
            }
        });
    }

    setupEvents() {
        // Close button
        this.closeBtn.addEventListener('click', () => this.hide());

        // Close on overlay click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.hide();
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('active')) {
                this.hide();
            }
        });

        // Navigation
        if (this.promotions.length > 1) {
            this.content.querySelector('.promo-popup-prev')?.addEventListener('click', () => this.prev());
            this.content.querySelector('.promo-popup-next')?.addEventListener('click', () => this.next());
        }

        // Pause autoplay on hover
        this.content.addEventListener('mouseenter', () => this.stopAutoPlay());
        this.content.addEventListener('mouseleave', () => this.startAutoPlay());
    }

    show() {
        this.modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.startAutoPlay();
    }

    hide() {
        this.modal.classList.remove('active');
        document.body.style.overflow = '';
        this.stopAutoPlay();
    }

    goToSlide(index) {
        const slides = this.content.querySelectorAll('.promo-slide');
        const dots = this.content.querySelectorAll('.promo-popup-dot');

        slides[this.currentIndex]?.classList.remove('active');
        dots[this.currentIndex]?.classList.remove('active');

        this.currentIndex = index;

        slides[this.currentIndex]?.classList.add('active');
        dots[this.currentIndex]?.classList.add('active');
    }

    next() {
        const nextIndex = (this.currentIndex + 1) % this.promotions.length;
        this.goToSlide(nextIndex);
    }

    prev() {
        const prevIndex = (this.currentIndex - 1 + this.promotions.length) % this.promotions.length;
        this.goToSlide(prevIndex);
    }

    startAutoPlay() {
        if (this.promotions.length <= 1) return;
        this.stopAutoPlay();
        this.autoPlayInterval = setInterval(() => this.next(), this.autoPlayDelay);
    }

    stopAutoPlay() {
        if (this.autoPlayInterval) {
            clearInterval(this.autoPlayInterval);
            this.autoPlayInterval = null;
        }
    }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
    // Always show promotions popup on page load (removed session check)
    const popup = new PromotionPopup();
    popup.init();
});

export { PromotionPopup };
